import React, { CSSProperties, useMemo } from 'react';
import { Card, CardContent, CardMedia, Chip, darken, makeStyles, Theme, Typography } from '@material-ui/core';
import { Entity } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { EntityBasicViewDesign, EntityBasicViewUIComponentCustomSettings } from '..';
import { useSelectProject } from '../../../../utils/dataStateHooks';
import { getDisplayableAttributes, getAttributeLabel } from '.';
import { useSelectTranslatedEntityClasses } from '../..';
import { useRitzTranslation } from '../../../../utils/customHooks';



const useDesign1Style = makeStyles((theme: Theme) => {
    return {
        root: {
            overflow      : 'hidden',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            height        : '100%',
            '&.clickable' : {
                cursor: 'pointer',
            },
        },

        image: {
            position       : 'relative',
            width          : 100,
            height         : '100%',
            minHeight      : 150,
            backgroundColor: '#ffffff',
            overflow       : 'hidden',
        },

        noImageMessage: {
            width         : '100%',
            height        : '100%',
            background    : darken(theme.palette.background.paper, 0.1),
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            textTransform : 'capitalize',
            padding       : theme.spacing(1),
        },

        icon: {
            position    : 'absolute',
            zIndex      : 2,
            bottom      : theme.spacing(1),
            right       : theme.spacing(1),
            width       : 30,
            height      : 30,
            boxShadow   : theme.shadows[2],
            background  : '#ffffff',
            borderRadius: '100%',
        },

        info: {
            maxWidth      : 'calc(100% - 100px)',
            height        : '100%',
            flex          : 1,
            display       : 'flex',
            flexDirection : 'column',
            justifyContent: 'center',
            alignItems    : 'flex-start',
            '& .class'    : {
                fontStyle: 'italic',
            },
        },

        attributesContainer: {
            maxWidth      : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            flexWrap      : 'wrap',
        },

        attribute: {
            marginRight : theme.spacing(1),
            marginBottom: theme.spacing(1),
            color       : theme.palette.text.primary,
            borderColor : theme.palette.text.primary,
            maxWidth    : '100%',
        },

        [theme.breakpoints.up('md')]: {
            image: {
                width: 150,
            },

            info: {
                maxWidth: 'calc(100% - 150px)',
            },

            icon: {
                width : 40,
                height: 40,
            },
        },
    };
}, { name: 'EntityBasicViewDesign1', index: 1 });



const useDesign2Style = makeStyles((theme: Theme) => {
    return {
        root: {
            overflow      : 'hidden',
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'flex-start',
            height        : '100%',
            boxShadow     : 'none',
            background    : 'none',
            '&.clickable' : {
                cursor: 'pointer',
            },
        },

        image: {
            width          : 150,
            height         : 150,
            borderRadius   : '100%',
            boxShadow      : theme.shadows[2],
            position       : 'relative',
            backgroundColor: '#ffffff',
            overflow       : 'hidden',
        },

        noImageMessage: {
            width         : '100%',
            height        : '100%',
            background    : darken(theme.palette.background.paper, 0.05),
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            textTransform : 'capitalize',
            padding       : theme.spacing(1),
        },

        icon: {
            position    : 'absolute',
            zIndex      : 2,
            bottom      : theme.spacing(0),
            right       : theme.spacing(2),
            width       : 40,
            height      : 40,
            boxShadow   : theme.shadows[2],
            background  : '#ffffff',
            borderRadius: '100%',
        },

        info: {
            maxWidth  : '100%',
            textAlign : 'center',
            '& .class': {
                fontStyle: 'italic',
            },
        },

        attributesContainer: {
            maxWidth      : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            flexWrap      : 'wrap',
        },

        attribute: {
            marginRight : theme.spacing(1),
            marginBottom: theme.spacing(1),
            color       : theme.palette.text.primary,
            borderColor : theme.palette.text.primary,
            maxWidth    : '100%',
        },

        [theme.breakpoints.up('md')]: {
            image: {
                width : 200,
                height: 200,
            },

            icon: {
                width : 50,
                height: 50,
            },
        },
    };
}, { name: 'EntityBasicViewDesign2', index: 1 });



export interface EntityBasicViewDesignOtherProps extends EntityBasicViewUIComponentCustomSettings {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly design: EntityBasicViewDesign.DESIGN_1 | EntityBasicViewDesign.DESIGN_2;
    readonly projectId: number;
    readonly selectedEntity: Entity;
    readonly texts: {
        readonly noImageMessage: string
    };
    readonly onClick?: ((event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined)=> void) | undefined;
}


export const EntityBasicViewDesignOther: React.FC<EntityBasicViewDesignOtherProps> = (props: EntityBasicViewDesignOtherProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const design1Classes = useDesign1Style();
    const design2Classes = useDesign2Style();
    const classes = props.design === 'Design_2' ? design2Classes : design1Classes;

    // Fetch entityClass data
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const entityClass = useMemo(() => {
        if (props.showEntityClass && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === props.selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, props.selectedEntity.entityClass, props.showEntityClass]);


    const displayableAttributes = useMemo((): {
        [key: string]: string
    } | undefined => {
        // If we don't need to show attributes or there are no attributes then return undefined
        if (!props.showAttributes || !props.selectedEntity.attributes) {
            return undefined;
        }

        // Call the attributesValuesString to return the string representation of the attributes
        return getDisplayableAttributes(props.selectedEntity, props.excludeAttributes, props.includeAttributes, props.excludeAttributeTypes);
    }, [props.excludeAttributeTypes, props.excludeAttributes, props.includeAttributes, props.selectedEntity, props.showAttributes]);

    // Get the imageUrl and iconUrl from entity instance and then fallback to entityClass and then fallback to defaultImage
    const imageUrl = props.selectedEntity.displaySettings.imageData?.url ?? entityClass?.displaySettings.imageData?.url ?? props.defaultImageUrl;
    const iconUrl = props.selectedEntity.displaySettings.iconData?.url ?? entityClass?.displaySettings.iconData?.url ?? props.defaultIconUrl;

    return (
        <Card
            className={clsx(props.className, classes.root)}
            style={{ ...props.style, ...props.containerStyle }}
            onClick={props.onClick}
        >
            {props.showImage !== false && (imageUrl || props.showNoImageMessage) && (
                <CardMedia
                    className={classes.image}
                    style={props.imageStyle}
                    image={imageUrl}
                    title={`${props.selectedEntity.displaySettings.name} Image`}
                >
                    {!imageUrl && props.showNoImageMessage && (
                        <div className={classes.noImageMessage}>
                            {props.texts.noImageMessage}
                        </div>
                    )}
                    {props.showIcon && iconUrl && (
                        <img
                            className={classes.icon}
                            style={props.iconStyle}
                            src={iconUrl}
                            alt={`${props.selectedEntity.displaySettings.name} Icon`}
                        />
                    )}
                </CardMedia>
            )}

            <CardContent
                className={classes.info}
                style={props.infoStyle}
            >
                {props.showEntityClass && (
                    <Typography
                        className="class"
                        component="p"
                        style={props.descriptionStyle}
                    >
                        {entityClass?.displaySettings.name}
                    </Typography>
                )}
                <Typography
                    gutterBottom
                    variant="h5"
                    component="h2"
                    style={props.nameStyle}
                >
                    {props.selectedEntity.displaySettings.name}
                </Typography>
                {props.showDescription !== false && props.selectedEntity.displaySettings.description && (
                    <Typography
                        component="p"
                        gutterBottom={displayableAttributes !== undefined}
                        style={props.descriptionStyle}
                    >
                        {props.selectedEntity.displaySettings.description}
                    </Typography>
                )}
                {props.showAttributes && displayableAttributes && (
                    <div className={classes.attributesContainer}>
                        {Object.entries(displayableAttributes).map(([key, value]) => {
                            const labelKey = getAttributeLabel(entityClass, key);
                            return (
                                <Chip
                                    className={classes.attribute}
                                    key={key}
                                    variant="outlined"
                                    size="small"
                                    label={<><b>{labelKey}:</b> {value}</>}
                                />
                            );
                        })}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
